import React from 'react';
import {
  ExclamationTriangleIcon,
  SparklesIcon,
  CheckIcon,
  XMarkIcon,
  AcademicCapIcon,
} from '@heroicons/react/24/outline';
import type {
  ObservationSuggestion,
  SuggestedOutcome,
  SuggestionSeverity,
} from '../../services/api/observations';

export interface SuggestionsPanelProps {
  suggestions: ObservationSuggestion[];
  /** Auto-suggested CAPS outcomes (discovery mode only). Empty in targeted mode. */
  suggestedOutcomes?: SuggestedOutcome[];
  isLoading?: boolean;
  /** Apply a single suggestion to the underlying text. The parent owns the
   * actual text-rewrite logic (replace evidence span with fix). */
  onAccept: (suggestion: ObservationSuggestion) => void;
  /** Drop a suggestion from the visible list without changing the text. */
  onDismiss: (suggestion: ObservationSuggestion) => void;
  /** Tag the observation with the chosen CAPS outcome. Optional -- when
   * the form doesn't expose an outcome field, the parent omits this and
   * the outcome section won't render its Apply button. */
  onAcceptOutcome?: (outcome: SuggestedOutcome) => void;
}

const issueLabel: Record<string, string> = {
  vague_phrasing: 'Vague phrasing',
  judgmental_language: 'Judgmental language',
  emotional_inference: 'Inferred emotion',
  missing_evidence: 'Missing evidence',
  length: 'Length',
  outcome_alignment: 'Outcome alignment',
};

const confidenceClass: Record<SuggestedOutcome['confidence'], string> = {
  high: 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-200 dark:border-emerald-800',
  medium: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800',
  low: 'bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-700',
};

const severityClass: Record<SuggestionSeverity, string> = {
  minor: 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/30 dark:text-amber-200 dark:border-amber-800',
  moderate: 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/30 dark:text-rose-200 dark:border-rose-800',
};

/** Side panel that renders specific issue/fix pairs from the local LLM.
 *
 * The deliberate UX choice here is *not* a wholesale rewrite: each item is
 * a discrete claim the teacher can accept or reject, preserving voice and
 * making the AI's reasoning legible. Wholesale rewrites are a commodity;
 * rubric-driven feedback against an early-childhood-observation guideline
 * is the differentiator.
 */
const SuggestionsPanel: React.FC<SuggestionsPanelProps> = ({
  suggestions,
  suggestedOutcomes = [],
  isLoading = false,
  onAccept,
  onDismiss,
  onAcceptOutcome,
}) => {
  if (isLoading) {
    return (
      <div
        className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4"
        data-testid="suggestions-panel-loading"
      >
        <div className="flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200">
          <SparklesIcon className="w-4 h-4 animate-pulse" aria-hidden="true" />
          Asking your local AI for suggestions… this can take a few seconds.
        </div>
      </div>
    );
  }

  if (suggestions.length === 0 && suggestedOutcomes.length === 0) {
    return (
      <div
        className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-4"
        data-testid="suggestions-panel-empty"
      >
        <div className="flex items-center gap-2 text-sm text-emerald-800 dark:text-emerald-200">
          <SparklesIcon className="w-4 h-4" aria-hidden="true" />
          The AI didn't flag anything — looks good.
        </div>
      </div>
    );
  }

  return (
    <div
      className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 space-y-0"
      data-testid="suggestions-panel"
    >
      {/* CAPS outcome suggestions render above the issue list. They're a
          different kind of action ("tag this observation against an
          outcome") and visually separating them prevents the teacher from
          conflating outcome choice with copy-edit suggestions. */}
      {suggestedOutcomes.length > 0 && (
        <div data-testid="suggested-outcomes-section">
          <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            CAPS outcome match{suggestedOutcomes.length === 1 ? '' : 'es'}
          </div>
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {suggestedOutcomes.map((outcome) => (
              <li key={outcome.outcome_id} className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <AcademicCapIcon
                      className="w-4 h-4 text-emerald-600 dark:text-emerald-400"
                      aria-hidden="true"
                    />
                    <span className="text-sm font-mono font-semibold text-gray-900 dark:text-gray-100">
                      {outcome.code}
                    </span>
                    <span
                      className={`inline-flex px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded border ${
                        confidenceClass[outcome.confidence] ?? confidenceClass.medium
                      }`}
                    >
                      {outcome.confidence}
                    </span>
                  </div>
                </div>

                <p className="text-sm text-gray-800 dark:text-gray-200">
                  {outcome.description}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400 italic">
                  Why: {outcome.rationale}
                </p>

                {onAcceptOutcome && (
                  <div className="flex gap-2 pt-1">
                    <button
                      type="button"
                      onClick={() => onAcceptOutcome(outcome)}
                      data-testid="outcome-accept"
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
                    >
                      <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" />
                      Tag with this outcome
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {suggestions.length > 0 && (
        <div className="px-4 py-2 border-y border-gray-200 dark:border-gray-700 text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
          {suggestions.length} writing suggestion{suggestions.length === 1 ? '' : 's'}
        </div>
      )}
      <ul className="divide-y divide-gray-200 dark:divide-gray-700">
        {suggestions.map((suggestion, index) => (
          <li key={`${suggestion.issue}-${index}`} className="p-4 space-y-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-2">
                <ExclamationTriangleIcon
                  className="w-4 h-4 text-gray-500 dark:text-gray-400"
                  aria-hidden="true"
                />
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                  {issueLabel[suggestion.issue] ?? suggestion.issue.replace(/_/g, ' ')}
                </span>
                <span
                  className={`inline-flex px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold rounded border ${
                    severityClass[suggestion.severity] ?? severityClass.minor
                  }`}
                >
                  {suggestion.severity}
                </span>
              </div>
            </div>

            {suggestion.evidence && (
              <p className="text-xs text-gray-600 dark:text-gray-400">
                <span className="opacity-70">In your text: </span>
                <span className="italic">"{suggestion.evidence}"</span>
              </p>
            )}

            <p className="text-sm text-gray-800 dark:text-gray-200">
              <span className="opacity-70 text-xs uppercase tracking-wide">Try: </span>
              {suggestion.fix}
            </p>

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={() => onAccept(suggestion)}
                data-testid="suggestion-accept"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
              >
                <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" />
                Apply
              </button>
              <button
                type="button"
                onClick={() => onDismiss(suggestion)}
                data-testid="suggestion-dismiss"
                className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                <XMarkIcon className="w-3.5 h-3.5" aria-hidden="true" />
                Dismiss
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default SuggestionsPanel;
